import angulartics from 'angulartics';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { EventService } from '@go-modules/services/event/event.service';
import type { GoEvent } from '@go-modules/services/event/event.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

interface PendingCaptionState {
	filename: string;
	caption_media_id: number;
}

export class AccessibilityAttachmentsController implements ng.IOnInit, ng.IOnDestroy {

	public media;
	public groupId: number;
	public captionFileOptions: any;
	public audioDescriptionOptions: any;

	private captionMedia = null;
	private audioDescriptionMedia = null;
	private pendingAudioDescriptionMedia = null;
	private eventSubscription: Subscription;
	private origCaptionMediaId: number;
	private latestUploadedFileName: string | null = null;
	private isAwaitingAudioDescriptionUpload = false;

	/** @ngInject */
	constructor (
		private MediaModel,
		private $translate: ng.translate.ITranslateService,
		private $log: ng.ILogService,
		private UniversalMediaChooser,
		private goModal,
		private MediaVTT,
		private eventService: EventService,
		private SupplementaryMedia,
		private confirmModal,
		private $window: ng.IWindowService,
		private $analytics: angulartics.IAnalyticsService,
		public helpUrls
	) {}

	public $onInit (): void {
		this.updateAccessibilityMedia();
		this.restorePendingCaptionState();
		this.initEventWatcher();
		this.initThumbnailOptions();
	}

	public $onDestroy (): void {
		this.eventSubscription.unsubscribe();
	}

	public addCaptionMedia () {
		this.openUMC('fine-uploader/src/partials/caption-only-whitelist.json', null, true)
			.then(this.onCaptionUpload)
			.catch(this.$log.error);
	}

	public addAudioDescriptionMedia () {
		this.latestUploadedFileName = null;
		this.pendingAudioDescriptionMedia = null;
		this.isAwaitingAudioDescriptionUpload = true;
		this.openUMC('fine-uploader/src/partials/audio-description-blacklist.json', this.MediaModel.TYPE.AUDIO)
			.then(this.onAudioDescriptionUpload)
			.catch(this.$log.error)
			.finally(() => {
				this.isAwaitingAudioDescriptionUpload = false;
			});
	}

	public removeCaptions () {
		return this.confirmModal.open({
			modalData: {
				title: this.$translate.instant('modal-confirm-delete-closed-caption_title'),
				message: this.$translate.instant('modal-confirm-delete-closed-caption_message'),
				yes: this.$translate.instant('common_delete'),
				no: this.$translate.instant('common_cancel')
			}
		}).result.then(() => {
			this.deleteCaptionMedia();
		}).catch(this.$log.error);
	}

	public removeAudioDescription () {
		return this.confirmModal.open({
			modalData: {
				title: this.$translate.instant('modal-confirm-delete-audio-description_title'),
				message: this.$translate.instant('modal-confirm-delete-audio-description_message'),
				yes: this.$translate.instant('common_delete'),
				no: this.$translate.instant('common_cancel')
			}
		}).result.then(() => {
			this.deleteAudioDescriptionMedia();
		}).catch(this.$log.error);
	}

	private updateAccessibilityMedia () {
		this.setCaptionMedia();
		this.setAudioDescriptionMedia();
	}

	private setAudioDescriptionMedia () {
		const previousAudioDescriptionMedia = this.audioDescriptionMedia;
		const nextAudioDescriptionMedia = this.media.getSupplementaryAudio().shift()
			|| this.pendingAudioDescriptionMedia;
		if (nextAudioDescriptionMedia && previousAudioDescriptionMedia) {
			nextAudioDescriptionMedia.filename_original = nextAudioDescriptionMedia.filename_original
				?? previousAudioDescriptionMedia.filename_original;
			nextAudioDescriptionMedia.filename = nextAudioDescriptionMedia.filename
				?? previousAudioDescriptionMedia.filename;
			if ((nextAudioDescriptionMedia.title == null
					|| nextAudioDescriptionMedia.title === ''
					|| nextAudioDescriptionMedia.title === 'Untitled')
					&& previousAudioDescriptionMedia.title) {
				nextAudioDescriptionMedia.title = previousAudioDescriptionMedia.title;
			}
		}
		if (nextAudioDescriptionMedia) {
			this.applyLatestUploadedAudioDescriptionName(nextAudioDescriptionMedia);
			if (nextAudioDescriptionMedia.media_status == null || nextAudioDescriptionMedia.media_status === '') {
				nextAudioDescriptionMedia.media_status = this.MediaModel.PENDING;
			}
		}
		if (this.shouldKeepAudioDescriptionPending(nextAudioDescriptionMedia)) {
			nextAudioDescriptionMedia.media_status = this.MediaModel.PENDING;
		}
		this.audioDescriptionMedia = nextAudioDescriptionMedia;
		if (nextAudioDescriptionMedia && nextAudioDescriptionMedia !== this.pendingAudioDescriptionMedia) {
			this.pendingAudioDescriptionMedia = null;
		}
	}

	private ensurePendingAudioDescriptionMedia (filename: string): void {
		if (!filename || this.media.getSupplementaryAudio().length > 0) {
			return;
		}

		if (!this.pendingAudioDescriptionMedia) {
			this.pendingAudioDescriptionMedia = this.SupplementaryMedia.newInstance(this.media.getId(), {
				media_type: this.MediaModel.TYPE.AUDIO,
				media_status: this.MediaModel.PENDING
			});
		}

		this.pendingAudioDescriptionMedia.filename_original = filename;
		this.pendingAudioDescriptionMedia.filename = filename;
		this.pendingAudioDescriptionMedia.title = filename;
		this.pendingAudioDescriptionMedia.media_status = this.MediaModel.PENDING;
		this.audioDescriptionMedia = this.pendingAudioDescriptionMedia;
	}

	private hasAudioDescriptionDisplayName (media): boolean {
		return Boolean(
			media?.filename_original
			|| media?.filename
		);
	}

	private applyLatestUploadedAudioDescriptionName (media): void {
		if (!media || !this.latestUploadedFileName || this.hasAudioDescriptionDisplayName(media)) {
			return;
		}
		media.filename_original = this.latestUploadedFileName;
		media.filename = media.filename || this.latestUploadedFileName;
		if (media.title == null || media.title === '' || media.title === 'Untitled') {
			media.title = this.latestUploadedFileName;
		}
	}

	private shouldKeepAudioDescriptionPending (media): boolean {
		if (!media || media.media_status !== this.MediaModel.READY) {
			return false;
		}
		return !this.hasAudioDescriptionDisplayName(media)
			|| media.media_url == null
			|| media.media_url === '';
	}

	private enforceAudioDescriptionPendingState (media, fallbackDisplayName: string | null = null): void {
		if (!media) {
			return;
		}
		if (!this.hasAudioDescriptionDisplayName(media) && fallbackDisplayName) {
			media.filename_original = fallbackDisplayName;
			media.filename = media.filename || fallbackDisplayName;
			if (media.title == null || media.title === '' || media.title === 'Untitled') {
				media.title = fallbackDisplayName;
			}
		}
		this.applyLatestUploadedAudioDescriptionName(media);
		if (media.media_status == null || media.media_status === '') {
			media.media_status = this.MediaModel.PENDING;
		}
		if (this.shouldKeepAudioDescriptionPending(media)) {
			media.media_status = this.MediaModel.PENDING;
		}
	}

	private getCaptionMedia () {
		const vtt = this.media.getMediaVTTCaptions().shift();
		const transformed = vtt != null ? this.MediaModel.transformMediaVttCaptionToMedia(vtt) : null;

		if (transformed !== null) {
			transformed.vtt_id = vtt.id;
			transformed.media_id = this.origCaptionMediaId || null;
		}

		return transformed;
	}

	private setCaptionMedia () {
		this.captionMedia = this.getCaptionMedia();
		if (this.captionMedia !== null && this.captionMedia.media_status === this.MediaModel.READY) {
			this.clearPendingCaptionState();
		}
	}

	private onCaptionUpload = (captionMedia) => {
		this.origCaptionMediaId = captionMedia.media_id;
		this.captionMedia = captionMedia;
		this.captionMedia.media_id = this.media.getId();
		const captionVtt = this.MediaVTT.transformMediaToMediaVttCaption(this.captionMedia);
		captionVtt.media_status = this.MediaModel.PENDING;
		this.media.addMediaVTT(captionVtt);
		this.persistPendingCaptionState({
			filename: this.captionMedia.filename,
			caption_media_id: this.origCaptionMediaId
		});
		this.setCaptionMedia();
	};

	private openUMC (blacklistFile, mediaType = null, includeVideoMediaId = false) {
		const umc = this.UniversalMediaChooser.get('umcModal');

		const defaultOptions: any = {
			headerOptions: [
				umc.MEDIA_TYPE.UPLOAD
			],
			defaultSection: umc.MEDIA_TYPE.UPLOAD,
			chooseAutomatically: true,
			uploadMinSizeLimit: 1,
			groupId: this.groupId,
			blacklist: null
		};

		if (blacklistFile) {
			defaultOptions.blacklist = blacklistFile;
		}

		if (includeVideoMediaId) {
			defaultOptions.extraParams = {
				video_media_id: this.media.getId()
			};
		}

		return this.goModal.open({
			modal: 'umc',
			modalData: {
				options: {
					mediaType,
					...defaultOptions
				}
			},
			animation: false
		}).result;
	}

	private initEventWatcher () {
		// Media sync event handler
		this.eventSubscription = this.eventService.events
			.pipe(filter((ev: GoEvent) =>
				ev.name === EVENT_NAMES.MEDIA_SYNC
				|| ev.name === EVENT_NAMES.FINE_UPLOADER_UPLOAD
				|| ev.name === EVENT_NAMES.FINE_UPLOADER_SUCCESS))
			.subscribe((ev: GoEvent) => {
				if (ev.name === EVENT_NAMES.FINE_UPLOADER_UPLOAD || ev.name === EVENT_NAMES.FINE_UPLOADER_SUCCESS) {
					if (!this.isAwaitingAudioDescriptionUpload) {
						return;
					}
					const uploadFileName = ev?.data?.file?.name;
					if (typeof uploadFileName === 'string' && uploadFileName !== '') {
						this.latestUploadedFileName = uploadFileName;
					}
					if (ev.name === EVENT_NAMES.FINE_UPLOADER_SUCCESS) {
						this.ensurePendingAudioDescriptionMedia(this.latestUploadedFileName || uploadFileName);
					}
					return;
				}

				// caption upload update (event fires for the caption media itself)
				const captionEventMediaId = this.origCaptionMediaId ?? this.captionMedia?.media_id;
				if (captionEventMediaId != null &&
					parseInt(captionEventMediaId, 10) === parseInt(ev.data.media_id, 10)) {
					const newStatus = ev.data.media_status === this.MediaModel.READY
						? this.MediaModel.READY
						: this.MediaModel.PENDING;
					this.captionMedia.media_status = newStatus;
					this.captionMedia.filename = ev.data.filename ?? this.captionMedia.filename;
					this.captionMedia.media_url = ev.data.media_url ?? this.captionMedia.media_url;

					this.media.media_vtt = this.media.media_vtt.map((item) => {
						const matchesExisting = item.id === this.captionMedia.vtt_id ||
							item.file === ev.data.filename_original ||
							item.file === this.captionMedia.filename;

						if (matchesExisting) {
							item.file = ev.data.filename ?? item.file;
							item.media_status = newStatus;
							item.media_url = ev.data.media_url ?? item.media_url;
						}
						return item;
					});

					this.setCaptionMedia();
					if (newStatus === this.MediaModel.READY) {
						this.clearPendingCaptionState();
						this.origCaptionMediaId = null;
					}
					return;
				}

				// video caption update (event fires for the parent video once captions attach)
				if (parseInt(this.media.getId(), 10) === parseInt(ev.data.media_id, 10) &&
					Array.isArray(ev.data.media_vtt)) {
					this.media.media_vtt = ev.data.media_vtt.map((item) => this.MediaVTT.newInstance(item));
					this.setCaptionMedia();
					this.clearPendingCaptionState();
				}

				if (this.media.supplementary_media && this.media.supplementary_media.length) {
					const existingSupplementaryMedia = this.media.supplementary_media[0];
					const supplementaryId = parseInt(existingSupplementaryMedia.supplementary_media_id, 10);
					// audio description update
					if (supplementaryId === parseInt(ev.data.media_id, 10)) {
						const mergedSupplementaryMediaData = {
							...existingSupplementaryMedia,
							...ev.data
						};
						mergedSupplementaryMediaData.filename_original = ev.data.filename_original
							?? existingSupplementaryMedia.filename_original;
						mergedSupplementaryMediaData.filename = ev.data.filename
							?? existingSupplementaryMedia.filename;
						if ((ev.data.title == null || ev.data.title === '' || ev.data.title === 'Untitled')
							&& existingSupplementaryMedia.title) {
							mergedSupplementaryMediaData.title = existingSupplementaryMedia.title;
						}
						this.applyLatestUploadedAudioDescriptionName(mergedSupplementaryMediaData);
						mergedSupplementaryMediaData.media_status = ev.data.media_status === this.MediaModel.READY
							? this.MediaModel.READY
							: this.MediaModel.PENDING;
						if (this.shouldKeepAudioDescriptionPending(mergedSupplementaryMediaData)) {
							mergedSupplementaryMediaData.media_status = this.MediaModel.PENDING;
						}
						const supplementaryMedia = this.SupplementaryMedia.newInstance(
							this.media.getId(),
							mergedSupplementaryMediaData
						);
						this.media.supplementary_media[0] = supplementaryMedia;

						// Refresh
						this.setAudioDescriptionMedia();
					}
				}
			});
	}

	private initThumbnailOptions () {
		// Media thumbnail options
		this.captionFileOptions = {
			actions: false,
			preventPreview: true,
			preventEdit: true,
			hideTitle: true
		};

		this.audioDescriptionOptions = {
			actions: false,
			preventEdit: true,
			hideTitle: true
		};
	}

	private deleteCaptionMedia () {
		const caption = this.media.media_vtt.find((item) => item.id === this.captionMedia?.vtt_id)
			?? this.media.media_vtt?.[0];
		const cleanup = () => {
			if (caption) {
				this.media.removeMediaVTT(caption);
			}
			this.$analytics.eventTrack('delete cc file');
			this.clearPendingCaptionState();
			this.origCaptionMediaId = null;
			this.setCaptionMedia();
		};

		if (caption?.id) {
			this.media
				.deleteMediaVTT(caption)
				.then(cleanup)
				.catch(this.$log.error);
			return;
		}

		if (this.origCaptionMediaId !== null && this.origCaptionMediaId !== undefined) {
			this.MediaModel.delete({ media_id: this.origCaptionMediaId })
				.$promise
				.then(cleanup)
				.catch(this.$log.error);
			return;
		}

		cleanup();
	}

	private deleteAudioDescriptionMedia () {
		if (this.pendingAudioDescriptionMedia && this.audioDescriptionMedia === this.pendingAudioDescriptionMedia) {
			this.pendingAudioDescriptionMedia = null;
			this.audioDescriptionMedia = null;
			return;
		}

		this.media
			.deleteSupplementaryMedia(this.audioDescriptionMedia)
			.then(() => {
				this.$analytics.eventTrack('delete ad file');
				this.setAudioDescriptionMedia();
			}).catch(this.$log.error);
	}

	private onAudioDescriptionUpload = (media) => {
		const supplementaryMedia = this.SupplementaryMedia.newInstance(this.media.getId(), media);
		const fallbackDisplayName = supplementaryMedia.filename_original
			|| supplementaryMedia.filename
			|| (supplementaryMedia.title && supplementaryMedia.title !== 'Untitled' ? supplementaryMedia.title : null)
			|| this.latestUploadedFileName;
		supplementaryMedia.filename_original = supplementaryMedia.filename_original
			|| supplementaryMedia.filename
			|| this.latestUploadedFileName;
		supplementaryMedia.filename = supplementaryMedia.filename || supplementaryMedia.filename_original;
		if ((supplementaryMedia.title == null || supplementaryMedia.title === '' || supplementaryMedia.title === 'Untitled')
			&& supplementaryMedia.filename_original) {
			supplementaryMedia.title = supplementaryMedia.filename_original;
		}
		supplementaryMedia.media_type = this.MediaModel.TYPE.AUDIO;
		supplementaryMedia.media_status = this.MediaModel.PENDING;
		this.media.addSupplementaryMedia(supplementaryMedia);
		supplementaryMedia.$save()
			.then(() => {
				supplementaryMedia.media_type = this.MediaModel.TYPE.AUDIO;
				this.enforceAudioDescriptionPendingState(supplementaryMedia, fallbackDisplayName);
				this.setAudioDescriptionMedia();
			})
			.catch(this.$log.error);
		this.setAudioDescriptionMedia();
	};

	private getPendingCaptionStorageKey (): string | null {
		return this.media?.getId ? `pending-caption-${this.media.getId()}` : null;
	}

	private persistPendingCaptionState (state: PendingCaptionState): void {
		const key = this.getPendingCaptionStorageKey();
		if (!key || !this.$window || !this.$window.sessionStorage) {
			return;
		}
		try {
			this.$window.sessionStorage.setItem(key, JSON.stringify(state));
		} catch (error) {}
	}

	private getPendingCaptionState (): PendingCaptionState | null {
		const key = this.getPendingCaptionStorageKey();
		if (!key || !this.$window || !this.$window.sessionStorage) {
			return null;
		}
		try {
			const stored = this.$window.sessionStorage.getItem(key);
			return stored ? JSON.parse(stored) : null;
		} catch (error) {
			return null;
		}
	}

	private clearPendingCaptionState (): void {
		const key = this.getPendingCaptionStorageKey();
		if (!key || !this.$window || !this.$window.sessionStorage) {
			return;
		}
		try {
			this.$window.sessionStorage.removeItem(key);
		} catch (error) {}
	}

	private restorePendingCaptionState (): void {
		const state = this.getPendingCaptionState();
		if (state === null
			|| this.media.getMediaVTTCaptions().length > 0
			|| this.captionMedia !== null) {
			if (this.media.getMediaVTTCaptions().length > 0) {
				this.clearPendingCaptionState();
			}
			return;
		}

		this.origCaptionMediaId = state.caption_media_id;
		const placeholder = this.MediaVTT.newInstance({
			file: state.filename,
			type: {
				name: this.MediaVTT.TYPE.CAPTION
			}
		});
		this.media.addMediaVTT(placeholder);
		this.setCaptionMedia();
	}
}
